import { createContext, useContext, ReactNode } from "react";
import { useNavigate } from "react-router-dom";

type NavigationContextType = {
  goToLoginPage: (replace?: boolean) => void;
  goToDashboard: (replace?: boolean) => void;
  goToEntryCompletionPage: (replace?: boolean) => void;
  goToRegisterPage: (replace?: boolean) => void;
  goToRandomizePage: (replace?: boolean) => void;
  goToDownloadPage: (replace?: boolean) => void;
  goToExportPage: (replace?: boolean) => void;
  goToSettingsPage: (replace?: boolean) => void;
  goToCheckoutPage: (replace?: boolean) => void;
  goToImportICalPage: (replace?: boolean) => void;
  goToForgotPasswordPage: (replace?: boolean) => void;
  goToPurchasePackagesPage: (productId?: string, replace?: boolean) => void;
  goToAICreditActivationPage: (replace?: boolean) => void;
  goToLicenseOverview: (replace?: boolean) => void;
  goBack: () => void;
  goToPage: (path: string, replace?: boolean) => void;
};

const NavigationContext = createContext<NavigationContextType>({
  goToLoginPage: () => {},
  goToDashboard: () => {},
  goToEntryCompletionPage: () => {},
  goToRegisterPage: () => {},
  goToRandomizePage: () => {},
  goToDownloadPage: () => {},
  goToExportPage: () => {},
  goToSettingsPage: () => {},
  goToPurchasePackagesPage: () => {},
  goToCheckoutPage: () => {},
  goToImportICalPage: () => {},
  goToForgotPasswordPage: () => {},
  goToAICreditActivationPage: () => {},
  goToLicenseOverview: () => {},
  goBack: () => {},
  goToPage: () => {},
});

export const useNavigationContext = () => useContext(NavigationContext);

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider = ({ children }: NavigationProviderProps) => {
  const navigate = useNavigate();

  const goToLoginPage = (replace?: boolean) => navigate("login", { replace });
  const goToDashboard = (replace?: boolean) =>
    navigate("dashboard", { replace: false });
  const goToRegisterPage = (replace?: boolean) =>
    navigate("register", { replace: false });
  const goToRandomizePage = (replace?: boolean) =>
    navigate("randomize", { replace: false });
  const goToEntryCompletionPage = (replace?: boolean) =>
    navigate("entryCompletion", { replace: false });
  const goToDownloadPage = (replace?: boolean) =>
    navigate("download", { replace: false });
  const goToExportPage = (replace?: boolean) =>
    navigate("export", { replace: false });
  const goToSettingsPage = (replace?: boolean) =>
    navigate("settings", { replace: false });
  const goToPurchasePackagesPage = (productId?: string) =>
    productId
      ? window.open(
          `https://www.berichtsheft-generator.com/kasse/?add-to-cart=${productId}`,
          "_blank"
        )
      : window.open(
          "https://www.berichtsheft-generator.com/produkt/ki-guthaben/",
          "_blank"
        );
  const goToLicenseOverview = (replace?: boolean) =>
    window.open(
      "https://www.berichtsheft-generator.com/mein-konto/view-license-keys/",
      "_blank"
    );
  const goToAICreditActivationPage = (replace?: boolean) =>
    navigate("activate", { replace: false });
  const goToCheckoutPage = (replace?: boolean) =>
    navigate("thank-you", { replace: false });
  const goToImportICalPage = (replace?: boolean) =>
    navigate("importICal", { replace: false });
  const goToForgotPasswordPage = (replace?: boolean) =>
    navigate("forgot", { replace: false });
  const goBack = () => navigate(-1); // for going back
  const goToPage = (path: string, replace?: boolean) =>
    navigate(path, { replace: false });

  const navigationContext: NavigationContextType = {
    goToLoginPage,
    goToDashboard,
    goToRegisterPage,
    goToRandomizePage,
    goToEntryCompletionPage,
    goToDownloadPage,
    goToExportPage,
    goToSettingsPage,
    goToPurchasePackagesPage,
    goToAICreditActivationPage,
    goToLicenseOverview,
    goToCheckoutPage,
    goToImportICalPage,
    goToForgotPasswordPage,
    goBack,
    goToPage,
  };

  return (
    <NavigationContext.Provider value={navigationContext}>
      {children}
    </NavigationContext.Provider>
  );
};
